import { PlanItem, Segment, NutritionTotals, NutrientGoalStatus } from '../types'
import { calculateSegmentPace } from './calculations'

export function calculateNutritionTotals(planItems: PlanItem[]): NutritionTotals {
  return planItems.reduce(
    (totals, item) => ({
      carbs_g: totals.carbs_g + (item.carbs_g || 0),
      fluids_ml: totals.fluids_ml + (item.fluids_ml || 0),
      sodium_mg: totals.sodium_mg + (item.sodium_mg || 0),
      caffeine_mg: totals.caffeine_mg + (item.caffeine_mg || 0)
    }),
    { carbs_g: 0, fluids_ml: 0, sodium_mg: 0, caffeine_mg: 0 }
  )
}

export function calculateTotalTime(segments: Segment[]): number {
  return segments.reduce((total, segment) => {
    const pace = calculateSegmentPace(segment)
    const segmentTime = (pace * segment.distance_km) + (segment.planned_stop_min || 0)
    return total + segmentTime
  }, 0)
}

export function calculatePerHourRates(
  segments: Segment[], 
  planItems: PlanItem[]
): NutritionTotals {
  const totalTimeHours = calculateTotalTime(segments) / 60
  const totals = calculateNutritionTotals(planItems)
  
  if (totalTimeHours === 0) {
    return { carbs_g: 0, fluids_ml: 0, sodium_mg: 0, caffeine_mg: 0 }
  }
  
  return {
    carbs_g: totals.carbs_g / totalTimeHours,
    fluids_ml: totals.fluids_ml / totalTimeHours,
    sodium_mg: totals.sodium_mg / totalTimeHours,
    caffeine_mg: totals.caffeine_mg / totalTimeHours
  }
}

export function calculateSegmentNutrition(
  segmentId: string,
  planItems: PlanItem[]
): NutritionTotals {
  const segmentItems = planItems.filter(item => item.segment_id === segmentId)
  return calculateNutritionTotals(segmentItems)
}

export function calculateAidStationNutrition(
  aidStationId: string,
  planItems: PlanItem[]
): NutritionTotals {
  const aidStationItems = planItems.filter(item => item.aid_station_id === aidStationId)
  return calculateNutritionTotals(aidStationItems)
}

export function validateNutritionTargets(
  perHourRates: NutritionTotals,
  targets: {
    carbs_g_per_h: { low: number; high: number }
    fluids_ml_per_h: { low: number; high: number }
    sodium_mg_per_h: { low: number; high: number }
  }
) {
  return {
    carbs: {
      status: perHourRates.carbs_g < targets.carbs_g_per_h.low ? 'low' : 
              perHourRates.carbs_g > targets.carbs_g_per_h.high ? 'high' : 'optimal',
      message: perHourRates.carbs_g < targets.carbs_g_per_h.low ? 
        'Consider adding more carbohydrates' :
        perHourRates.carbs_g > targets.carbs_g_per_h.high ?
        'Too many carbohydrates may cause GI issues' :
        'Carbohydrate intake looks good'
    },
    fluids: {
      status: perHourRates.fluids_ml < targets.fluids_ml_per_h.low ? 'low' : 
              perHourRates.fluids_ml > targets.fluids_ml_per_h.high ? 'high' : 'optimal',
      message: perHourRates.fluids_ml < targets.fluids_ml_per_h.low ? 
        'Increase fluid intake to prevent dehydration' :
        perHourRates.fluids_ml > targets.fluids_ml_per_h.high ?
        'High fluid intake may cause hyponatremia' :
        'Fluid intake looks appropriate'
    },
    sodium: {
      status: perHourRates.sodium_mg < targets.sodium_mg_per_h.low ? 'low' : 
              perHourRates.sodium_mg > targets.sodium_mg_per_h.high ? 'high' : 'optimal',
      message: perHourRates.sodium_mg < targets.sodium_mg_per_h.low ? 
        'Add electrolytes to prevent cramping' :
        perHourRates.sodium_mg > targets.sodium_mg_per_h.high ?
        'High sodium intake may cause issues' :
        'Electrolyte balance looks good'
    }
  }
}

/**
 * Get nutrition icons/symbols for compact display
 */
export function getNutritionIcons(planItems: PlanItem[]): string {
  const icons: string[] = []
  const hasSnack = planItems.some(item => item.type === 'snack')
  const hasDrink = planItems.some(item => item.type === 'drink')
  const hasNote = planItems.some(item => item.type === 'note')

  if (hasSnack) icons.push('🍌')
  if (hasDrink) icons.push('💧')
  if (hasNote) icons.push('📝')

  return icons.slice(0, 3).join(' ')
}

/**
 * Calculate nutrition goal status for a specific segment
 */
export function calculateSegmentGoalStatus(
  segmentTimeMinutes: number,
  segmentNutrition: NutritionTotals,
  targets: {
    carbs_g_per_h: { low: number; high: number }
    fluids_ml_per_h: { low: number; high: number }
    sodium_mg_per_h: { low: number; high: number }
  }
) {
  const segmentTimeHours = segmentTimeMinutes / 60

  // Calculate expected nutrition for this segment's duration
  const expectedNutrition = {
    carbs: {
      low: targets.carbs_g_per_h.low * segmentTimeHours,
      high: targets.carbs_g_per_h.high * segmentTimeHours
    },
    fluids: {
      low: targets.fluids_ml_per_h.low * segmentTimeHours,
      high: targets.fluids_ml_per_h.high * segmentTimeHours
    },
    sodium: {
      low: targets.sodium_mg_per_h.low * segmentTimeHours,
      high: targets.sodium_mg_per_h.high * segmentTimeHours
    }
  }

  // Calculate status for each nutrient
  const carbs = {
    status: (segmentNutrition.carbs_g < expectedNutrition.carbs.low ? 'low' :
            segmentNutrition.carbs_g > expectedNutrition.carbs.high ? 'high' : 'optimal') as NutrientGoalStatus,
    expected: expectedNutrition.carbs,
    actual: segmentNutrition.carbs_g,
    deficit: Math.max(0, expectedNutrition.carbs.low - segmentNutrition.carbs_g),
    excess: Math.max(0, segmentNutrition.carbs_g - expectedNutrition.carbs.high)
  }

  const fluids = {
    status: (segmentNutrition.fluids_ml < expectedNutrition.fluids.low ? 'low' :
            segmentNutrition.fluids_ml > expectedNutrition.fluids.high ? 'high' : 'optimal') as NutrientGoalStatus,
    expected: expectedNutrition.fluids,
    actual: segmentNutrition.fluids_ml,
    deficit: Math.max(0, expectedNutrition.fluids.low - segmentNutrition.fluids_ml),
    excess: Math.max(0, segmentNutrition.fluids_ml - expectedNutrition.fluids.high)
  }

  const sodium = {
    status: (segmentNutrition.sodium_mg < expectedNutrition.sodium.low ? 'low' :
            segmentNutrition.sodium_mg > expectedNutrition.sodium.high ? 'high' : 'optimal') as NutrientGoalStatus,
    expected: expectedNutrition.sodium,
    actual: segmentNutrition.sodium_mg,
    deficit: Math.max(0, expectedNutrition.sodium.low - segmentNutrition.sodium_mg),
    excess: Math.max(0, segmentNutrition.sodium_mg - expectedNutrition.sodium.high)
  }

  return { carbs, fluids, sodium, segmentTimeHours }
}

/**
 * Format nutrition summary for display
 */
export function formatNutritionSummary(totals: NutritionTotals): string {
  const parts: string[] = []

  if (totals.carbs_g > 0) {
    parts.push(`${Math.round(totals.carbs_g)}g carbs`)
  }
  if (totals.fluids_ml > 0) {
    parts.push(`${Math.round(totals.fluids_ml)}ml fluids`)
  }
  if (totals.sodium_mg > 0) {
    parts.push(`${Math.round(totals.sodium_mg)}mg sodium`)
  }
  if (totals.caffeine_mg > 0) {
    parts.push(`${Math.round(totals.caffeine_mg)}mg caffeine`)
  }

  return parts.join(', ')
}